import React from "react";
import { connect } from "react-redux";
import { FormattedMessage, injectIntl } from "react-intl";
import RentalProductRow from "./RentalProductRow";
import Roles from "../../enums/Roles";
import Paginator from "../sub/Paginator";
import ProductsParserUtil from "../../util/ProductsParserUtil";
import { Link } from "react-router-dom";
import ArrayUtil from "../../util/ArrayUtil";
import "../../css/products/products.css";
import "../../css/rentals/RentalProducts.css";
import { Alert } from "react-bootstrap";
import Icon from "../sub/Icon";
import TableToolbar from "../sub/bootstrap/TableToolbar";
import { getProductsByMercurialIdAdmin } from "../../actions/products/products";
import { getProducts } from "../../actions/products/products";

class RentalProducts extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      search: "",
      ...this.parseProductParams(),
      searchMercurialId: this.props.match?.params?.mercurialId || "",
      imageRefreshTimestamp: Date.now(),
    };

    this.paginator = new Paginator(this);
    this.paginationAdjusted = false; // Flag pour éviter les ajustements multiples
    this.scrollPending = false; // Flag pour gérer le scroll en attente
  }

  componentDidMount() {
    // Charger les produits selon le rôle utilisateur
    const produitsDeMemeMercuriale = this.props.products.filter(
      (p) => p.mercurial_id === this.state.searchMercurialId,
    );

    if (
      this.props.products.length === 0 ||
      produitsDeMemeMercuriale.length === 0
    ) {
      if (
        this.props.user.role === Roles.ADMIN ||
        this.props.user.role === Roles.SALES_REP
      ) {
        if (this.state.searchMercurialId) {
          this.props.onGetProductsByMercurialIdAdmin(
            this.state.searchMercurialId,
          );
        }
      } else {
        this.props.onGetProducts();
      }
    } else {
      // Si on a déjà les produits et un searchProductId, ajuster la pagination
      if (this.state.searchProductId) {
        this.scrollPending = true;
        this.adjustPaginationForProduct();
      }
    }
  }

  componentDidUpdate(prevProps, prevState) {
    // Phase 1: Gérer les changements de pagination d'abord
    const searchProductIdChanged =
      this.state.searchProductId &&
      this.state.searchProductId !== prevState.searchProductId;
    const productsLoaded = this.props.products !== prevProps.products;

    if (searchProductIdChanged) {
      this.paginationAdjusted = false;
      this.scrollPending = true;
      this.adjustPaginationForProduct();
      return; // Sortir tôt pour permettre à la pagination de se terminer
    }

    if (
      productsLoaded &&
      this.state.searchProductId &&
      !this.paginationAdjusted
    ) {
      this.scrollPending = true;
      this.adjustPaginationForProduct();
      return; // Sortir tôt pour permettre à la pagination de se terminer
    }

    // Phase 2: Gérer le scroll après que la pagination soit stable
    if (
      this.state.searchProductId &&
      this.paginationAdjusted &&
      this.scrollPending
    ) {
      this.scrollPending = false;
      this.scrollToProduct();
    }
  }

  parseProductParams() {
    const params = this.props.match?.params || {};
    const { famille, product } = params;

    if (!famille) {
      return {
        familleComplete: "",
        searchProductId: null,
      };
    }

    const decodedFamille = decodeURIComponent(famille);

    // Si product est fourni, c'est l'ID du produit
    if (product) {
      const isObjectId = /^[0-9a-fA-F]{24}$/.test(product);
      if (isObjectId) {
        return {
          familleComplete: decodedFamille,
          searchProductId: product,
        };
      }
    }

    return {
      familleComplete: decodedFamille,
      searchProductId: null,
    };
  }

  isFavoriteFamily(famille) {
    return famille === "Favoris";
  }

  adjustPaginationForProduct() {
    if (!this.state.searchProductId || this.paginationAdjusted) {
      return;
    }

    // Filtrer les produits pour obtenir la même liste que dans render()
    let filtedProductsOnMercurialId = this.props.products.filter((p) => {
      const matchesMercurial =
        !this.state.searchMercurialId ||
        p.mercurial_id === this.state.searchMercurialId;
      const isLocationFamily =
        p.famille && p.famille.toLowerCase().includes("location");
      return matchesMercurial && isLocationFamily;
    });

    // Filtrer par famille si spécifiée (toujours, même pendant la recherche)
    const famille = this.state.familleComplete;
    if (famille && !this.isFavoriteFamily(famille)) {
      filtedProductsOnMercurialId = filtedProductsOnMercurialId.filter(
        (p) => p.famille === famille,
      );
    }

    // Parser les produits
    let products = ProductsParserUtil.parseProducts(
      filtedProductsOnMercurialId,
      !this.isFavoriteFamily(famille) ? famille : "",
    );

    // Filtrer par recherche avec critères étendus
    if (this.state.search && this.state.search.trim() !== "") {
      const searchTerm = this.state.search.toLowerCase().trim();
      products = products.filter((product) => {
        return (
          (product.sous_famille &&
            product.sous_famille.toLowerCase().includes(searchTerm)) ||
          (product.designation &&
            product.designation.toLowerCase().includes(searchTerm)) ||
          (product.caracteristiques &&
            product.caracteristiques.toLowerCase().includes(searchTerm)) ||
          (product.famille &&
            product.famille.toLowerCase().includes(searchTerm))
        );
      });
    }

    if (products.length > 0) {
      const targetProductIndex = products.findIndex(
        (product) => product._id === this.state.searchProductId,
      );

      if (targetProductIndex !== -1) {
        // Calculer sur quelle page se trouve ce produit (en commençant à 1)
        const targetPage =
          Math.floor(targetProductIndex / this.paginator.paginationElems) + 1;
        // Mettre à jour l'index de pagination pour afficher la bonne page
        this.paginator.updateIndex(targetPage);
        // Marquer la pagination comme ajustée pour éviter les ajustements futurs
        this.paginationAdjusted = true;
      }
    }
  }

  scrollToProduct() {
    // Utiliser requestAnimationFrame pour s'assurer que le DOM est complètement rendu
    setTimeout(() => {
      requestAnimationFrame(() => {
        const productElement = document.getElementById(
          `product-${this.state.searchProductId}`,
        );

        if (productElement) {
          // Vérifier que l'élément est visible et positionné
          const rect = productElement.getBoundingClientRect();
          if (rect.height > 0 && rect.width > 0) {
            // Scroll vers le produit
            productElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });

            // Appliquer la mise en surbrillance
            productElement.style.backgroundColor = "#fff3cd";
            setTimeout(() => {
              productElement.style.backgroundColor = "";
            }, 3000);
          } else {
            // Si l'élément n'est pas encore visible, réessayer après un court délai
            setTimeout(() => this.scrollToProduct(), 100);
          }
        }
      });
    }, 200); // Délai plus long pour s'assurer que la pagination est terminée
  }

  render() {
    var famille = this.state.familleComplete;
    let isRendered = true;
    let favorites_found;

    // Filtrer les produits contenant "location" dans la famille
    // Si on a un mercurial_id dans l'URL mais qu'on affiche une famille spécifique,
    // on affiche TOUS les produits de cette famille peu importe leur mercurial_id
    let filtedProductsOnMercurialId = this.props.products.filter((p) => {
      const isLocationFamily =
        p.famille && p.famille.toLowerCase().includes("location");

      // Si on affiche une famille spécifique (pas les favoris), on ignore le filtre mercurial_id
      // pour afficher TOUS les produits de la famille
      if (famille && !this.isFavoriteFamily(famille)) {
        return isLocationFamily;
      }

      // Sinon, on applique le filtre mercurial_id comme avant
      const matchesMercurial =
        !this.state.searchMercurialId ||
        p.mercurial_id === this.state.searchMercurialId;

      return matchesMercurial && isLocationFamily;
    });

    var dateColumn = (
      <th className="d-none d-md-table-cell col-3 rental-products-table-header">
        Période de location
      </th>
    );
    var validateColumn = (
      <th className="d-none d-md-table-cell col-2 rental-products-table-header"></th>
    );
    var dateColumnResponsive = <th className="d-md-none col-2">Période</th>;

    // Vérifier si on doit afficher les produits
    switch (true) {
      case filtedProductsOnMercurialId.length === 0:
        isRendered = false;
        break;
      case this.isFavoriteFamily(famille):
        let favoriteRefs =
          this.props.user.favorite_rental_products?.map(
            (value) => value.ref_frn,
          ) || [];

        if (favoriteRefs.length !== 0) {
          let products_ref_frn = filtedProductsOnMercurialId.map(
            (value) => value.ref_frn,
          );
          favorites_found = ArrayUtil.intersection(
            products_ref_frn,
            favoriteRefs,
          );
        }

        isRendered = favorites_found && favorites_found.length !== 0;
        break;
      default:
        break;
    }

    // Filtrer par famille si spécifiée (toujours, même pendant la recherche)
    if (famille && !this.isFavoriteFamily(famille)) {
      filtedProductsOnMercurialId = filtedProductsOnMercurialId.filter(
        (p) => p.famille === famille,
      );
    }

    // Parser les produits
    let products = ProductsParserUtil.parseProducts(
      filtedProductsOnMercurialId,
      !this.isFavoriteFamily(famille)
        ? famille
        : isRendered
          ? favorites_found
          : "",
    );

    // Filtrer par recherche avec critères étendus
    if (this.state.search && this.state.search.trim() !== "") {
      const searchTerm = this.state.search.toLowerCase().trim();
      products = products.filter((product) => {
        return (
          (product.sous_famille &&
            product.sous_famille.toLowerCase().includes(searchTerm)) ||
          (product.designation &&
            product.designation.toLowerCase().includes(searchTerm)) ||
          (product.caracteristiques &&
            product.caracteristiques.toLowerCase().includes(searchTerm)) ||
          (product.famille &&
            product.famille.toLowerCase().includes(searchTerm))
        );
      });
    }

    this.paginator.init(products.length);

    let productsNode = products.map((product) => {
      if (!this.isFavoriteFamily(famille)) {
        // Filtrer par famille toujours (même pendant la recherche)
        if (famille && famille !== "" && product.famille !== famille) {
          return null;
        }
      } else {
        if (favorites_found.indexOf(product.ref_frn) === -1) {
          return null;
        }
      }

      if (!this.paginator.keep()) return null;

      return (
        <RentalProductRow
          product={product}
          searchProductId={this.state.searchProductId}
          imageRefreshTimestamp={this.state.imageRefreshTimestamp}
          key={`${product._id}-${this.state.imageRefreshTimestamp}`}
        />
      );
    });

    if (
      this.props.user.role === Roles.SALES_REP ||
      this.props.user.role === Roles.ADMIN
    ) {
      dateColumn = null;
      validateColumn = null;
      dateColumnResponsive = null;
    }

    // Déterminer si on vient de la mercuriale (présence de mercurialId dans l'URL)
    // Mais si le paramètre ?from=rentals est présent, on vient de la rubrique location
    const searchParams = new URLSearchParams(this.props.location?.search || "");
    const isFromRentals = searchParams.get("from") === "rentals";
    const isFromMercurial = !!this.state.searchMercurialId && !isFromRentals;

    // Déterminer le lien de retour selon l'origine
    // - Favoris ou mercuriale → retour aux familles
    // - Rubrique location → retour aux locations
    const shouldShowFamiliesButton =
      this.isFavoriteFamily(famille) || isFromMercurial;
    const backLink = shouldShowFamiliesButton
      ? "/home/families"
      : "/home/rentals";
    const backText = shouldShowFamiliesButton
      ? "Retour aux familles"
      : "Retour aux locations";

    // Nom de la famille pour les favoris de location
    const favoriteFamilyName = this.props.intl
      .formatMessage({ id: "Top.Rental.Products" })
      .toUpperCase();
    const searchPlaceholder = this.props.intl.formatMessage({ id: "Search" });

    if (!isRendered) {
      // Pour les favoris, utiliser le même style que Products.js
      if (this.isFavoriteFamily(famille)) {
        return (
          <React.Fragment>
            <TableToolbar>
              <Link to={backLink} className="btn btn-info">
                <Icon icon="circle-arrow-left" className="mr-1" />
                <FormattedMessage id="Categories" />
              </Link>
              <span className="btn-default ml-4">
                {favoriteFamilyName}
                <span className="pl-1">
                  <strong>(0)</strong>
                </span>
              </span>
              <input
                className="form-control ml-auto w-50"
                type="search"
                value={this.state.search}
                onChange={(e) => this.setState({ search: e.target.value })}
                placeholder={searchPlaceholder}
              />
            </TableToolbar>
            <Alert variant="info" className="mt-3">
              Aucun produit de location favori trouvé.
            </Alert>
          </React.Fragment>
        );
      }

      return (
        <div className="container-fluid mt-3">
          <div className="d-flex align-items-center mb-3">
            <Link
              to={backLink}
              className="btn btn-outline-secondary btn-sm me-3"
            >
              <Icon icon="arrow-left" /> {backText}
            </Link>
            <h2 className="mb-0">
              Produits de location
              {famille && famille !== "" && (
                <small className="text-muted"> - {famille}</small>
              )}
            </h2>
          </div>
          <Alert variant="info">Aucun produit de location trouvé.</Alert>
        </div>
      );
    }

    // Affichage différent pour les favoris (même style que Products.js)
    if (this.isFavoriteFamily(famille)) {
      return (
        <React.Fragment>
          <TableToolbar>
            <Link to={backLink} className="btn btn-info">
              <Icon icon="circle-arrow-left" className="mr-1" />
              <FormattedMessage id="Categories" />
            </Link>
            <span className="btn-default ml-4">
              {favoriteFamilyName}
              <span className="pl-1">
                <strong>({products.length})</strong>
              </span>
            </span>
            <input
              className="form-control ml-auto w-50"
              type="search"
              value={this.state.search}
              onChange={(e) => {
                this.setState({ search: e.target.value });
                this.paginator.updateIndex(1);
              }}
              placeholder={searchPlaceholder}
            />
          </TableToolbar>

          <table className="table tablee4coll mt-3">
            <thead>
              <tr className="tableHeader d-flex">
                <th style={{ width: "50px" }}></th>
                <th className="col-2 text-center">Image</th>
                <th className="col">Désignation</th>
                <th className="d-none d-md-table-cell col-1 text-end">
                  Prix HT
                </th>
                <th className="d-none d-md-table-cell col-1 text-end">
                  Prix TTC
                </th>
                {dateColumn}
                {validateColumn}
                {dateColumnResponsive}
              </tr>
            </thead>

            <tbody>
              {products.map((product, index) => {
                if (!this.paginator.keep()) return null;

                return (
                  <RentalProductRow
                    key={product._id || index}
                    product={product}
                    index={index}
                    searchProductId={this.state.searchProductId}
                    imageRefreshTimestamp={this.state.imageRefreshTimestamp}
                  />
                );
              })}
            </tbody>
          </table>
          {this.paginator.render()}
        </React.Fragment>
      );
    }

    // Si on vient de la mercuriale, utiliser le style TableToolbar comme pour les favoris
    if (isFromMercurial) {
      return (
        <React.Fragment>
          <TableToolbar>
            <Link to={backLink} className="btn btn-info">
              <Icon icon="circle-arrow-left" className="mr-1" />
              <FormattedMessage id="Categories" />
            </Link>
            <span className="btn-default ml-4">
              {famille.toUpperCase()}
              <span className="pl-1">
                <strong>({products.length})</strong>
              </span>
            </span>
            <input
              className="form-control ml-auto w-50"
              type="search"
              value={this.state.search}
              onChange={(e) => {
                this.setState({ search: e.target.value });
                this.paginator.updateIndex(1);
              }}
              placeholder={searchPlaceholder}
            />
          </TableToolbar>

          {products.length === 0 ? (
            <div className="alert alert-info mt-4" role="alert">
              {this.state.search
                ? `Aucun produit ne correspond à votre recherche "${this.state.search}"`
                : "Aucun produit disponible"}
            </div>
          ) : (
            <>
              <table className="table tablee4coll mt-3">
                <thead>
                  <tr className="tableHeader d-flex">
                    <th style={{ width: "50px" }}></th>
                    <th className="col-2 text-center">Image</th>
                    <th className="col">Désignation</th>
                    <th className="d-none d-md-table-cell col-1 text-end">
                      Prix HT
                    </th>
                    <th className="d-none d-md-table-cell col-1 text-end">
                      Prix TTC
                    </th>
                    {dateColumn}
                    {validateColumn}
                    {dateColumnResponsive}
                  </tr>
                </thead>
                <tbody>{productsNode}</tbody>
              </table>

              {this.paginator.render()}
            </>
          )}
        </React.Fragment>
      );
    }

    // Style par défaut pour la rubrique location
    return (
      <React.Fragment>
        <div className="container-fluid mt-3">
          <div className="rental-products-header">
            <div className="d-flex align-items-center">
              <Link
                to={backLink}
                className="btn btn-link text-decoration-none rental-products-back-link"
              >
                <Icon icon="arrow-left" /> {backText}
              </Link>
            </div>
            <div className="rental-products-title-container">
              <h1 className="rental-products-title">
                {famille && famille !== "" && famille}
              </h1>
            </div>
            <div className="rental-products-count-badge">
              {products.length} produit{products.length > 1 ? "s" : ""}
            </div>
          </div>

          <div className="rental-products-search-container">
            <input
              id="search"
              type="text"
              className="form-control rental-products-search-input"
              placeholder="Rechercher dans tous les produits de location..."
              value={this.state.search}
              onChange={(e) => {
                this.setState({ search: e.target.value });
                this.paginator.updateIndex(1);
              }}
            />
            {this.state.search && (
              <div className="rental-products-search-results">
                <small>
                  <i className="fas fa-search me-1"></i>
                  Résultats pour "{this.state.search}" : {products.length}{" "}
                  produit{products.length !== 1 ? "s" : ""} trouvé
                  {products.length !== 1 ? "s" : ""}
                </small>
              </div>
            )}
          </div>

          {products.length === 0 ? (
            <div className="alert alert-info mt-4" role="alert">
              <i className="fas fa-info-circle me-2"></i>
              {this.state.search
                ? `Aucun produit ne correspond à votre recherche "${this.state.search}"`
                : "Aucun produit disponible"}
            </div>
          ) : (
            <>
              <table className="table align-middle rental-products-table">
                <thead>
                  <tr className="d-flex">
                    <th
                      className="rental-products-table-header"
                      style={{ width: "50px" }}
                    ></th>
                    <th className="col-2 text-center rental-products-table-header">
                      Image
                    </th>
                    <th className="col rental-products-table-header">
                      Désignation
                    </th>
                    <th className="d-none d-md-table-cell col-1 text-end rental-products-table-header">
                      Prix HT
                    </th>
                    <th className="d-none d-md-table-cell col-1 text-end rental-products-table-header">
                      Prix TTC
                    </th>
                    {dateColumn}
                    {validateColumn}
                    {dateColumnResponsive}
                  </tr>
                </thead>
                <tbody>{productsNode}</tbody>
              </table>

              {this.paginator.render()}
            </>
          )}
        </div>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    user: state.user,
    products: state.products,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    onGetProducts: () => dispatch(getProducts()),
    onGetProductsByMercurialIdAdmin: (mercurialId) =>
      dispatch(getProductsByMercurialIdAdmin(mercurialId)),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(injectIntl(RentalProducts));
